import axios from 'config/axios';
import setAuthToken from 'utils/setAuthToken';
import { authManagedAnswer, signInThroughAuthIfManaged } from 'utils/lsgoAuthenticationSchools';
import { isLsgoAuthenticationSession } from 'utils/lsgoAuthenticationSession';
import store from '../store';
import { GET_ERRORS } from '../store/actions/actionTypes';

/** Legacy refused the session (409 AUTH_MANAGED): drop every legacy token. */
function dropLegacySession() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('lsgoRT');
    delete axios.defaults.headers.common['x-refresh-token'];
    setAuthToken(false);
}

class Auth {
    // Check auth token for expiry and existence to run before all API calls
    static async checkCurrentAuthToken() {
        // An lsgo-authentication session renews itself (utils/lsgoAuthenticationSession.js).
        if (isLsgoAuthenticationSession()) return;
        await Auth.setNewAuthToken();
    }

    static async setNewAuthToken() {
        return await Auth.userV2({ redirect: false, history: null });
    }

    /**
     * Legacy session renewal. If legacy refuses it because the student's school
     * is on lsgo-authentication (409 AUTH_MANAGED), the legacy tokens are
     * dropped and, unless `followAuthManaged` is false (App.js tries the auth
     * session restore first), auth sign-in starts for that school. Resolves
     * { authManaged } (no token) in that case.
     */
    static async userV2({ redirect, history, redirectUri, followAuthManaged = true }) {
        const onAuthManaged = async (respOrError) => {
            const answer = authManagedAnswer(respOrError);
            if (!answer) return null;
            dropLegacySession();
            if (followAuthManaged) await signInThroughAuthIfManaged(respOrError);
            return { authManaged: answer };
        };
        try {
            return await axios.get('/api/v4/auth/user').then(async (resp) => {
                const managed = await onAuthManaged(resp);
                if (managed) return managed;
                if (resp && resp.data) {
                    if (resp.data.error) {
                        throw new Error(resp.data.error);
                    }
                    const { success, token } = resp.data;
                    if (success) {
                        setAuthToken(token);
                        if (
                            redirectUri &&
                            typeof redirectUri === 'string' &&
                            (redirectUri.includes('/checkin') || redirectUri.includes('/quiet-place'))
                        ) {
                            if (history) history.push(redirectUri);
                            else window.location.href = redirectUri;
                        } else {
                            if (redirect && history) history.push('/');
                            if (redirect && !history) window.location.href = '/';
                        }
                        return token;
                    }
                } else if (resp && resp.response && +resp.response.status === 401) {
                    localStorage.removeItem('authToken');
                }
            });
        } catch (err) {
            const managed = await onAuthManaged(err);
            if (managed) return managed;
            store.dispatch({
                type: GET_ERRORS,
                payload: err.message,
            });
            return { error: err.message };
        }
    }
}

export default Auth;
